import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, Users, Inbox, AlertTriangle, CornerUpLeft } from "lucide-react";
import { api, fmtDay } from "../lib/api";
import { PageHeader, Panel } from "../components/ui-kit";
import { RoleBadge } from "../components/Badges";

const TaskLine = ({ t, showRole }) => (
  <Link to={`/tasks/${t.id}`} data-testid={`workload-task-${t.id}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-[#1C202E]">
    <span className="min-w-0 flex-1 truncate font-medium">{t.title}</span>
    {t.sent_back && <CornerUpLeft className="h-3.5 w-3.5 text-rose-400" title="Sent back" />}
    <span className="text-xs text-slate-500">Step {t.current_step + 1}/{t.total_steps} · {t.current_step_name}</span>
    {showRole && <RoleBadge role={t.current_role} />}
    <span className={`font-mono text-[11px] ${t.overdue ? "font-semibold text-rose-300" : "text-slate-400"}`}>{t.overdue ? "Overdue " : "Due "}{fmtDay(t.due_date)}</span>
  </Link>
);

const Row = ({ id, title, sub, count, overdue, tasks, showRole }) => {
  const [open, setOpen] = useState(false);
  return (
    <Panel data-testid={`workload-row-${id}`} className="overflow-hidden">
      <button data-testid={`workload-toggle-${id}`} onClick={() => setOpen(!open)} disabled={!count} className="flex w-full items-center gap-4 p-4 text-left transition-colors hover:bg-[#1C202E] disabled:cursor-default disabled:hover:bg-transparent">
        <div className="min-w-0 flex-1">
          <div className="truncate font-semibold">{title}</div>
          <div className="mt-1 flex flex-wrap gap-1">{sub}</div>
        </div>
        {overdue > 0 && <span data-testid={`workload-overdue-${id}`} className="inline-flex items-center gap-1 rounded-md border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 font-mono text-[10px] text-rose-300"><AlertTriangle className="h-3 w-3" />{overdue} overdue</span>}
        <span data-testid={`workload-count-${id}`} className={`font-heading text-3xl font-bold ${count ? "text-[#FF6B00]" : "text-slate-600"}`}>{count}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""} ${count ? "" : "invisible"}`} />
      </button>
      {open && <div className="border-t border-[#282E3D] p-2">{tasks.map((t) => <TaskLine key={t.id} t={t} showRole={showRole} />)}</div>}
    </Panel>
  );
};

const Stat = ({ label, value, id, danger }) => (
  <Panel className="p-5">
    <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div data-testid={`workload-total-${id}`} className={`mt-2 font-heading text-4xl font-bold ${danger && value ? "text-rose-300" : ""}`}>{value}</div>
  </Panel>
);

export default function Workload() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  const [hideIdle, setHideIdle] = useState(false);
  useEffect(() => { api.get("/workload").then((r) => setD(r.data)).catch(() => setErr(true)); }, []);
  if (err) return <div className="text-rose-300" data-testid="workload-error">Couldn't load workload. Refresh to try again.</div>;
  if (!d) return <div className="text-slate-500">Loading…</div>;
  return (
    <div>
      <PageHeader eyebrow="Workload" title="Who has what" desc="Pending tasks held by each person, and what's waiting in each role's shared queue. Click a row to see the tasks." />
      <div className="mb-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat id="held" label="Held by people" value={d.totals.held} />
        <Stat id="queued" label="Waiting in queues" value={d.totals.queued} />
        <Stat id="overdue" label="Overdue (pending)" value={d.totals.overdue} danger />
      </div>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Users className="h-4 w-4 text-[#FF6B00]" />Pending with people
            <button data-testid="workload-hide-idle-toggle" onClick={() => setHideIdle(!hideIdle)} className={`ml-auto rounded-full border px-3 py-1 text-xs font-medium transition-colors ${hideIdle ? "border-[#FF6B00] bg-[#FF6B00] text-white" : "border-[#282E3D] text-slate-400 hover:text-white"}`}>Hide people with 0</button>
          </h2>
          <div className="space-y-3" data-testid="workload-people">
            {d.people.filter((p) => !hideIdle || p.count).map((p) => (
              <Row key={p.user.id} id={`user-${p.user.id}`} title={p.user.name} count={p.count} overdue={p.overdue} tasks={p.tasks} showRole
                sub={p.user.is_admin ? <span className="text-xs text-orange-300">Super Admin</span> : p.user.roles?.length ? p.user.roles.map((r) => <RoleBadge key={r} role={r} />) : <span className="text-xs text-slate-600">No roles</span>} />
            ))}
          </div>
        </section>
        <section>
          <h2 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold"><Inbox className="h-4 w-4 text-[#FF6B00]" />Waiting in shared queues</h2>
          <div className="space-y-3" data-testid="workload-queues">
            {d.queues.map((q) => <Row key={q.role} id={`role-${q.role}`} title={<RoleBadge role={q.role} />} count={q.count} overdue={q.overdue} tasks={q.tasks} sub={<span className="text-xs text-slate-500">Not picked up yet</span>} />)}
          </div>
        </section>
      </div>
    </div>
  );
}
